import { Component, type ReactNode } from 'react';

const RELOADED = 'ofc:reloaded-for-update';

/** The browsers' various ways of saying a code file could not be fetched. */
function isMissingFile(error: unknown): boolean {
  const text = error instanceof Error ? `${error.name} ${error.message}` : String(error);
  return /dynamically imported module|module script failed|Importing a module script|Failed to fetch|ChunkLoadError|error loading dynamically/i.test(
    text,
  );
}

/** Reloads the page once per session; false if it has already tried. */
export function reloadOnce(): boolean {
  try {
    if (sessionStorage.getItem(RELOADED)) return false;
    sessionStorage.setItem(RELOADED, String(Date.now()));
  } catch {
    return false;
  }
  location.reload();
  return true;
}

/**
 * The last line of defence against a blank screen.
 *
 * Each game is its own file, named after its contents, so a phone that opened
 * the page before a deploy asks for names that no longer exist. That is caught
 * here and fixed with one reload. Anything else shows a way out instead of
 * nothing at all.
 */
export class Recover extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: unknown): void {
    if (isMissingFile(error) && reloadOnce()) return;
    console.error('the app hit an error it could not recover from', error);
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    return (
      <main className="screen" style={{ display: 'grid', placeItems: 'center' }}>
        <section className="panel col center" style={{ gap: 10, padding: 20, maxWidth: 360 }}>
          <div className="logo neon neon-pink" style={{ fontSize: 26 }}>
            Something went wrong
          </div>
          <p className="small muted" style={{ margin: 0 }}>
            Reloading usually sorts it. If you were in a game, your seat and score are kept.
          </p>
          <button className="btn-primary" onClick={() => location.reload()}>
            Reload
          </button>
        </section>
      </main>
    );
  }
}
